import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@vimaana/ui";
import type {
  BelegVorlage,
  Firmenstamm,
  FirmenstammAntwort,
  KatalogPosition,
  Nummernkreis,
  Textbausteine,
} from "../lib/types";

/**
 * Pflege der Belegstammdaten.
 *
 * Firmenstamm, Textbausteine, Positionskatalog und Einheiten liegen als
 * Dateien neben der PDF-Engine — dieselben, die auch die Desktop-App liest.
 * Wer hier speichert, ändert beide Apps. Der Nummernkreis liegt in Postgres.
 *
 * Geschrieben wird per POST statt PUT: die CORS-Allowlist des Webhooks kennt
 * kein PUT (dieselbe Regel wie im Verträge-Planer).
 */

const BASIS = "/api/kontor/rechnungen/stammdaten";

/** Nach jeder Änderung muss der Editor seine Bausteine neu ziehen. */
function useStammdatenInvalidierung() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["stammdaten"] });
    queryClient.invalidateQueries({ queryKey: ["invoices", "editor-options"] });
    queryClient.invalidateQueries({ queryKey: ["invoices", "catalog"] });
    queryClient.invalidateQueries({ queryKey: ["invoices", "number"] });
  };
}

export function useFirmenstamm() {
  return useQuery<FirmenstammAntwort>({
    queryKey: ["stammdaten", "firmenstamm"],
    queryFn: () => apiGet(`${BASIS}/firmenstamm-vorschau`),
    staleTime: 60_000,
  });
}

export function useSaveFirmenstamm() {
  const invalidieren = useStammdatenInvalidierung();
  return useMutation({
    mutationFn: (profil: Firmenstamm) => apiPost<{ ok: boolean }>(`${BASIS}/firmenstamm`, profil),
    onSuccess: invalidieren,
  });
}

export function useTextbausteine() {
  return useQuery<Textbausteine>({
    queryKey: ["stammdaten", "textbausteine"],
    queryFn: () => apiGet(`${BASIS}/textbausteine`),
    staleTime: 60_000,
  });
}

export function useSaveTextbausteine() {
  const invalidieren = useStammdatenInvalidierung();
  return useMutation({
    mutationFn: (daten: Textbausteine) => apiPost<Textbausteine>(`${BASIS}/textbausteine`, daten),
    onSuccess: invalidieren,
  });
}

/** Der VOLLE Katalog — die Suche im Editor kappt bei 50 Treffern. */
export function useKatalog() {
  return useQuery<{ items: KatalogPosition[] }>({
    queryKey: ["stammdaten", "katalog"],
    queryFn: () => apiGet(`${BASIS}/katalog`),
    staleTime: 60_000,
  });
}

export function useSaveKatalog() {
  const invalidieren = useStammdatenInvalidierung();
  return useMutation({
    mutationFn: (items: KatalogPosition[]) => apiPost<{ items: KatalogPosition[] }>(`${BASIS}/katalog`, { items }),
    onSuccess: invalidieren,
  });
}

export function useEinheiten() {
  return useQuery<{ items: string[] }>({
    queryKey: ["stammdaten", "einheiten"],
    queryFn: () => apiGet(`${BASIS}/einheiten`),
    staleTime: 60_000,
  });
}

export function useSaveEinheiten() {
  const invalidieren = useStammdatenInvalidierung();
  return useMutation({
    mutationFn: (items: string[]) => apiPost<{ items: string[] }>(`${BASIS}/einheiten`, { items }),
    onSuccess: invalidieren,
  });
}

export function useNummernkreise() {
  return useQuery<{ items: Nummernkreis[] }>({
    queryKey: ["stammdaten", "nummernkreis"],
    queryFn: () => apiGet(`${BASIS}/nummernkreis`),
    staleTime: 30_000,
  });
}

/**
 * `force` übergeht die Rückwärts-Sperre des Servers. Nur setzen, wenn der
 * Nutzer den Warnhinweis ausdrücklich bestätigt hat — ein zurückgedrehter
 * Zähler erzeugt doppelte Belegnummern.
 */
export function useSaveNummernkreis() {
  const invalidieren = useStammdatenInvalidierung();
  return useMutation({
    mutationFn: ({ kind, patch, force }: {
      kind: string;
      patch: Partial<Nummernkreis>;
      force?: boolean;
    }) => apiPost<{ ok: boolean; kreise: Nummernkreis[] }>(
      `${BASIS}/nummernkreis?kind=${encodeURIComponent(kind)}${force ? "&force=true" : ""}`,
      patch,
    ),
    onSuccess: invalidieren,
  });
}

/* ══ Beleg-Vorlagen ══════════════════════════════════════════════════════ */

export function useVorlagen() {
  return useQuery<{ items: BelegVorlage[] }>({
    queryKey: ["stammdaten", "vorlagen"],
    queryFn: () => apiGet("/api/kontor/rechnungen/vorlagen"),
    staleTime: 60_000,
  });
}

export function useSaveVorlage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => (
      apiPost<BelegVorlage>("/api/kontor/rechnungen/vorlagen", body)
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["stammdaten", "vorlagen"] }),
  });
}

export function useDeleteVorlage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/rechnungen/vorlagen/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["stammdaten", "vorlagen"] }),
  });
}
